import { timeStampNow, clocksNow, timeStampToClocks } from '@datadog/js-core/time'
import { deepClone, mockable } from '@datadog/js-core/util'
import type { Duration, TimeStamp, RelativeTime } from '@datadog/js-core/time'
import type {
  DeflateWorker,
  DeflateEncoderStreamId,
  DeflateEncoder,
  PublicApi,
  ContextManager,
  TrackingConsent,
  User,
  Account,
  RumInternalContext,
  Telemetry,
  Encoder,
  ResourceType,
  SessionManager,
} from '@datadog/browser-core'
import type { Context } from '@datadog/js-core/util'
import {
  ContextManagerMethod,
  addTelemetryUsage,
  makePublicApi,
  createHandlingStack,
  sanitize,
  createIdentityEncoder,
  displayAlreadyInitializedError,
  createTrackingConsentState,
  CustomerContextKey,
  defineContextMethod,
  startBufferingData,
  generateUUID,
  display,
} from '@datadog/browser-core'
import { monitor, callMonitored } from '@datadog/js-core/monitor'

import type { LifeCycle } from '../domain/lifeCycle'
import type { ViewHistory } from '../domain/contexts/viewHistory'
import type { ReplayStats } from '../rawRumEvent.types'
import { ActionType, VitalType } from '../rawRumEvent.types'
import { DEFAULT_TRACKED_RESOURCE_HEADERS } from '../domain/configuration'
import type { RumConfiguration, RumInitConfiguration } from '../domain/configuration'
import type { ViewOptions } from '../domain/view/trackViews'
import type {
  AddDurationVitalOptions,
  DurationVitalOptions,
  OperationOptions,
  FeatureOperationOptions,
  FailureReason,
} from '../domain/vital/vitalCollection'
import { callPluginsOnRumStart } from '../domain/plugins'
import type { Hooks } from '../domain/hooks'
import type { SdkName } from '../domain/contexts/defaultContext'
import type { ActionOptions } from '../domain/action/trackManualActions'
import type { ResourceOptions, ResourceStopOptions } from '../domain/resource/trackManualResources'
import { createPreStartStrategy } from './preStartRum'
import type { StartRumResult } from './startRum'
import { startRum } from './startRum'

export interface StartRecordingOptions {
  force: boolean
}

/**
 * Public API for the RUM browser SDK.
 *
 * See [RUM Browser Monitoring Setup](https://docs.datadoghq.com/real_user_monitoring/browser) for further information.
 *
 * @category Main
 */
export interface RumPublicApi extends PublicApi {
  /**
   * Init the RUM browser SDK.
   *
   * See [RUM Browser Monitoring Setup](https://docs.datadoghq.com/real_user_monitoring/browser) for further information.
   *
   * @category Init
   * @param initConfiguration - Configuration options of the SDK
   * @example
   * ```ts
   * datadogRum.init({
   *   applicationId: '<DATADOG_APPLICATION_ID>',
   *   clientToken: '<DATADOG_CLIENT_TOKEN>',
   *   site: '<DATADOG_SITE>',
   *   // ...
   * })
   * ```
   */
  init: (initConfiguration: RumInitConfiguration) => void

  /**
   * Set the tracking consent of the current user.
   *
   * Data will be sent only if it is set to "granted". This value won't be stored by the library
   * across page loads: you will need to call this method or set the appropriate `trackingConsent`
   * field in the init() method at each page load.
   *
   * If this method is called before the init() method, the provided value will take precedence
   * over the one provided as initialization parameter.
   *
   * See [User tracking consent](https://docs.datadoghq.com/real_user_monitoring/browser/advanced_configuration/#user-tracking-consent) for further information.
   *
   * @category Privacy
   * @param trackingConsent - The user tracking consent
   */
  setTrackingConsent: (trackingConsent: TrackingConsent) => void

  /**
   * Set View Name.
   *
   * Enable to manually change the name of the current view.
   * See [Override default RUM view names](https://docs.datadoghq.com/real_user_monitoring/browser/advanced_configuration/#override-default-rum-view-names) for further information.
   *
   * @category Context - View
   * @param name - Name of the view
   */
  setViewName: (name: string) => void

  /**
   * Set View Context.
   *
   * Enable to manually set the context of the current view.
   *
   * @category Context - View
   * @param context - Context of the view
   */
  setViewContext: (context: Context) => void
  /**
   * Set View Context Property.
   *
   * Enable to manually set a property of the context of the current view.
   *
   * @category Context - View
   * @param key - key of the property
   * @param value - value of the property
   */
  setViewContextProperty: (key: string, value: any) => void

  /**
   * Get View Context.
   *
   * @category Context - View
   */
  getViewContext(): Context

  /**
   * [Internal API] Get the internal SDK context
   *
   * @internal
   */
  getInternalContext: (startTime?: number) => RumInternalContext | undefined

  /**
   * Get the init configuration
   *
   * @category Init
   * @returns RumInitConfiguration | undefined
   */
  getInitConfiguration: () => RumInitConfiguration | undefined

  /**
   * Add a custom action, stored in `@action`
   *
   * See [Send RUM Custom Actions](https://docs.datadoghq.com/real_user_monitoring/guide/send-rum-custom-actions) for further information.
   *
   * @category Data Collection
   * @param name - Name of the action
   * @param context - Context of the action
   */
  addAction: (name: string, context?: object) => void

  /**
   * Start tracking a custom action.
   *
   * Call {@link stopAction} with the same name (and optional `actionKey`) to send a RUM action event
   * with the elapsed duration. Errors and resources triggered between start and stop are associated
   * with the action.
   *
   * @category Data Collection
   * @param name - Name of the action
   * @param options - Options of the action (@default type: 'custom')
   * @example
   * ```ts
   * datadogRum.startAction('checkout', { context: { cartId: 'abc' } })
   * // ... user completes checkout
   * datadogRum.stopAction('checkout')
   * ```
   */
  startAction: (name: string, options?: ActionOptions) => void

  /**
   * Stop tracking a custom action started with {@link startAction}.
   *
   * Sends a RUM action event with the elapsed duration since the matching start call. Context from
   * start and stop calls is merged into the event.
   *
   * @category Data Collection
   * @param name - Name of the action
   * @param options - Options of the action
   */
  stopAction: (name: string, options?: ActionOptions) => void

  /**
   * Start tracking a resource manually.
   *
   * Use this for network activity that the SDK cannot automatically instrument. Call {@link stopResource}
   * with the same URL (and optional `resourceKey`) to send a RUM resource event with the elapsed duration.
   *
   * @category Data Collection
   * @param url - URL of the resource
   * @param options - Options of the resource (@default type: 'other')
   * @example
   * ```ts
   * datadogRum.startResource('https://api.example.com/users', { type: 'fetch', method: 'POST' })
   * // ... perform the request
   * datadogRum.stopResource('https://api.example.com/users', { statusCode: 201 })
   * ```
   */
  startResource: (url: string, options?: ResourceOptions) => void

  /**
   * Stop tracking a resource started with {@link startResource}.
   *
   * Sends a RUM resource event with the elapsed duration since the matching start call. Context from
   * start and stop calls is merged into the event.
   *
   * @category Data Collection
   * @param url - URL of the resource
   * @param options - Options of the resource
   */
  stopResource: (url: string, options?: ResourceStopOptions) => void

  /**
   * Add a custom error, stored in `@error`.
   *
   * See [Send RUM Custom Actions](https://docs.datadoghq.com/real_user_monitoring/guide/send-rum-custom-actions) for further information.
   *
   * @category Data Collection
   * @param error - Error. Favor sending a [Javascript Error](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Error) to have a stack trace attached to the error event.
   * @param context - Context of the error
   */
  addError: (error: unknown, context?: object) => void

  /**
   * Add a custom timing relative to the start of the current view,
   * stored in `@view.custom_timings.<timing_name>`
   *
   * Note: passing a relative time is discouraged since it is actually used as-is but displayed relative to the view start.
   * We currently don't provide a way to retrieve the view start time, so it can be challenging to provide a timing relative to the view start.
   * see https://github.com/DataDog/browser-sdk/issues/2552
   *
   * @category Data Collection
   * @param name - Name of the custom timing
   * @param [time] - Epoch timestamp of the custom timing (if not set, will use current time)
   */
  addTiming: (name: string, time?: number) => void

  /**
   * [Experimental] Manually set the current view's loading time.
   *
   * Call this method when the view has finished loading. The loading time is computed as the
   * elapsed time since the view started. Each call replaces any previously set value (last-call-wins).
   *
   * @category Data Collection
   */
  setViewLoadingTime: () => void

  /**
   * Set the global context information to all events, stored in `@context`
   * See [Global context](https://docs.datadoghq.com/real_user_monitoring/browser/advanced_configuration/#global-context) for further information.
   *
   * @category Context - Global Context
   * @param context - Global context
   */
  setGlobalContext: (context: Context) => void

  /**
   * Get the global Context
   *
   * See [Global context](https://docs.datadoghq.com/real_user_monitoring/browser/advanced_configuration/#global-context) for further information.
   *
   * @category Context - Global Context
   */
  getGlobalContext: () => Context

  /**
   * Set or update a global context property, stored in `@context.<key>`
   *
   * See [Global context](https://docs.datadoghq.com/real_user_monitoring/browser/advanced_configuration/#global-context) for further information.
   *
   * @category Context - Global Context
   * @param key - Key of the property
   * @param value - Value of the property
   */
  setGlobalContextProperty: (key: any, value: any) => void

  /**
   * Remove a global context property
   *
   * See [Global context](https://docs.datadoghq.com/real_user_monitoring/browser/advanced_configuration/#global-context) for further information.
   *
   * @category Context - Global Context
   */
  removeGlobalContextProperty: (key: any) => void

  /**
   * Clear the global context
   *
   * See [Global context](https://docs.datadoghq.com/real_user_monitoring/browser/advanced_configuration/#global-context) for further information.
   *
   * @category Context - Global Context
   */
  clearGlobalContext(): void

  /**
   * Set user information to all events, stored in `@usr`
   *
   * See [User session](https://docs.datadoghq.com/real_user_monitoring/browser/advanced_configuration/#user-session) for further information.
   *
   * @category Context - User
   * @param newUser - User information
   */
  setUser(newUser: User & { id: string }): void

  /**
   * Set user information to all events, stored in `@usr`
   *
   * @category Context - User
   * @deprecated You must specify a user id, favor using {@link setUser} instead
   * @param newUser - User information with optional id
   */
  setUser(newUser: User): void

  /**
   * Get user information
   *
   * See [User session](https://docs.datadoghq.com/real_user_monitoring/browser/advanced_configuration/#user-session) for further information.
   *
   * @category Context - User
   * @returns User information
   */
  getUser: () => Context

  /**
   * Set or update the user property, stored in `@usr.<key>`
   *
   * See [User session](https://docs.datadoghq.com/real_user_monitoring/browser/advanced_configuration/#user-session) for further information.
   *
   * @category Context - User
   * @param key - Key of the property
   * @param property - Value of the property
   */
  setUserProperty: (key: any, property: any) => void

  /**
   * Remove a user property
   *
   * @category Context - User
   * @param key - Key of the property to remove
   * @see [User session](https://docs.datadoghq.com/real_user_monitoring/browser/advanced_configuration/#user-session) for further information.
   */
  removeUserProperty: (key: any) => void

  /**
   * Clear all user information
   *
   * See [User session](https://docs.datadoghq.com/real_user_monitoring/browser/advanced_configuration/#user-session) for further information.
   *
   * @category Context - User
   */
  clearUser: () => void

  /**
   * Set account information to all events, stored in `@account`
   *
   * @category Context - Account
   * @param newAccount - Account information
   */
  setAccount: (newAccount: Account) => void

  /**
   * Get account information
   *
   * @category Context - Account
   * @returns Account information
   */
  getAccount: () => Context

  /**
   * Set or update the account property, stored in `@account.<key>`
   *
   * @category Context - Account
   * @param key - Key of the property
   * @param property - Value of the property
   */
  setAccountProperty: (key: string, property: any) => void

  /**
   * Remove an account property
   *
   * @category Context - Account
   * @param key - Key of the property to remove
   */
  removeAccountProperty: (key: string) => void

  /**
   * Clear all account information
   *
   * @category Context - Account
   * @returns Clear all account information
   */
  clearAccount: () => void
  /**
   * Start a view manually.
   * Enable to manual start a view, use `trackViewsManually: true` init parameter and call `startView()` to create RUM views and be aligned with how you’ve defined them in your SPA application routing.
   *
   * See [Override default RUM view names](https://docs.datadoghq.com/real_user_monitoring/browser/advanced_configuration/#override-default-rum-view-names) for further information.
   *
   * Context - @category Data Collection
   *
   * @param nameOrOptions - The view name, or a {@link ViewOptions} object to configure the view
   */
  startView(nameOrOptions?: string | ViewOptions): void

  /**
   * Stop the session. A new session will start at the next user interaction with the page.
   *
   * @category Session
   */
  stopSession(): void

  /**
   * Add a feature flag evaluation,
   * stored in `@feature_flags.<feature_flag_key>`
   *
   * We recommend enabling the intake request compression when using feature flags `compressIntakeRequests: true`.
   *
   * See [Feature Flag Tracking](https://docs.datadoghq.com/real_user_monitoring/feature_flag_tracking/) for further information.
   *
   * @category Data Collection
   * @param key - The key of the feature flag.
   * @param value - The value of the feature flag.
   */
  addFeatureFlagEvaluation: (key: string, value: any) => void

  /**
   * Get the Session Replay Link.
   *
   * See [Connect Session Replay To Your Third-Party Tools](https://docs.datadoghq.com/real_user_monitoring/guide/connect-session-replay-to-your-third-party-tools) for further information.
   *
   * @category Session Replay
   */
  getSessionReplayLink: () => string | undefined

  /**
   * Start Session Replay recording.
   * Enable to conditionally start the recording, use the `startSessionReplayRecordingManually:true` init parameter and call `startSessionReplayRecording()`
   *
   * See [Browser Session Replay](https://docs.datadoghq.com/real_user_monitoring/session_replay/browser) for further information.
   *
   * @category Session Replay
   */
  startSessionReplayRecording: (options?: StartRecordingOptions) => void

  /**
   * Stop Session Replay recording.
   *
   * See [Browser Session Replay](https://docs.datadoghq.com/real_user_monitoring/session_replay/browser) for further information.
   *
   * @category Session Replay
   */
  stopSessionReplayRecording: () => void

  /**
   * Add a custom duration vital
   *
   * @category Vital - Duration
   * @param name - Name of the custom vital
   * @param options - Options for the custom vital (startTime, duration, context, description)
   */
  addDurationVital: (name: string, options: AddDurationVitalOptions) => void

  /**
   * Start a custom duration vital.
   *
   * If you plan to have multiple durations for the same vital, use the `vitalKey` option to
   * differentiate them. Provide the same key when calling `stopDurationVital`.
   *
   * @category Vital - Duration
   * @param name - Name of the custom vital
   * @param options - Options for the custom vital (vitalKey, context, description)
   * @example
   * ```ts
   * // Simple usage
   * datadogRum.startDurationVital('my-vital')
   * datadogRum.stopDurationVital('my-vital')
   *
   * // Multiple simultaneous vitals with the same name
   * const key = crypto.randomUUID()
   * datadogRum.startDurationVital('my-vital', { vitalKey: key })
   * datadogRum.stopDurationVital('my-vital', { vitalKey: key })
   * ```
   */
  startDurationVital: (name: string, options?: DurationVitalOptions) => void

  /**
   * Stop a custom duration vital
   *
   * @category Vital - Duration
   * @param name - Name of the custom vital
   * @param options - Options for the custom vital (vitalKey, context, description)
   */
  stopDurationVital: (name: string, options?: DurationVitalOptions) => void

  /**
   * Start an operation.
   *
   * Call {@link succeedOperation} or {@link failOperation} with the same name (and optional
   * `operationKey`) to send a RUM vital event marking the end of the operation.
   *
   * @category Vital - Operation
   * @param name - Name of the operation
   * @param options - Options for the operation (operationKey, context, description)
   * @example
   * ```ts
   * datadogRum.startOperation('checkout')
   * // ... perform the operation
   * datadogRum.succeedOperation('checkout')
   * ```
   */
  startOperation: (name: string, options?: OperationOptions) => void

  /**
   * Mark an operation as successful.
   *
   * Sends a RUM vital event marking the end of the operation started with {@link startOperation}.
   *
   * @category Vital - Operation
   * @param name - Name of the operation
   * @param options - Options for the operation (operationKey, context, description)
   */
  succeedOperation: (name: string, options?: OperationOptions) => void

  /**
   * Mark an operation as failed.
   *
   * Sends a RUM vital event marking the end of the operation started with {@link startOperation}.
   *
   * @category Vital - Operation
   * @param name - Name of the operation
   * @param failureReason - Reason for the failure
   * @param options - Options for the operation (operationKey, context, description)
   */
  failOperation: (name: string, failureReason: FailureReason, options?: OperationOptions) => void

  /**
   * Start a feature operation.
   *
   * @category Vital - Feature Operation
   * @deprecated Use {@link startOperation} instead.
   * @param name - Name of the operation
   * @param options - Options for the operation (operationKey, context, description)
   */
  startFeatureOperation: (name: string, options?: FeatureOperationOptions) => void

  /**
   * Mark a feature operation as successful.
   *
   * @category Vital - Feature Operation
   * @deprecated Use {@link succeedOperation} instead.
   * @param name - Name of the operation
   * @param options - Options for the operation (operationKey, context, description)
   */
  succeedFeatureOperation: (name: string, options?: FeatureOperationOptions) => void

  /**
   * Mark a feature operation as failed.
   *
   * @category Vital - Feature Operation
   * @deprecated Use {@link failOperation} instead.
   * @param name - Name of the operation
   * @param failureReason - Reason for the failure
   * @param options - Options for the operation (operationKey, context, description)
   */
  failFeatureOperation: (name: string, failureReason: FailureReason, options?: FeatureOperationOptions) => void

  /**
   * List of default headers used by the {@link RumInitConfiguration.trackResourceHeaders | trackResourceHeaders} option.
   *
   * @deprecated You can now omit `name` from a MatchHeader entry to include default headers.
   */
  DEFAULT_TRACKED_RESOURCE_HEADERS: typeof DEFAULT_TRACKED_RESOURCE_HEADERS
}

export interface RecorderApi {
  start: (options?: StartRecordingOptions) => void
  stop: () => void
  onRumStart: (
    lifeCycle: LifeCycle,
    configuration: RumConfiguration,
    sessionManager: SessionManager,
    viewHistory: ViewHistory,
    deflateWorker: DeflateWorker | undefined,
    telemetry: Telemetry
  ) => void
  isRecording: () => boolean
  getReplayStats: (viewId: string) => ReplayStats | undefined
  getSessionReplayLink: () => string | undefined
}

export interface ProfilerApi {
  stop: () => void
  onRumStart: (
    lifeCycle: LifeCycle,
    hooks: Hooks,
    configuration: RumConfiguration,
    sessionManager: SessionManager,
    viewHistory: ViewHistory,
    createEncoder: (streamId: DeflateEncoderStreamId) => Encoder
  ) => void
}

export interface RumPublicApiOptions {
  ignoreInitIfSyntheticsWillInjectRum?: boolean
  startDeflateWorker?: (
    configuration: RumConfiguration,
    source: string,
    onInitializationFailure: () => void
  ) => DeflateWorker | undefined
  createDeflateEncoder?: (worker: DeflateWorker, streamId: DeflateEncoderStreamId) => DeflateEncoder
  sdkName?: SdkName
}

export interface Strategy {
  init: (initConfiguration: RumInitConfiguration, publicApi: RumPublicApi, errorStack?: string) => void
  initConfiguration: RumInitConfiguration | undefined
  getInternalContext: StartRumResult['getInternalContext']
  stopSession: StartRumResult['stopSession']
  addTiming: StartRumResult['addTiming']
  setLoadingTime: StartRumResult['setLoadingTime']
  startView: StartRumResult['startView']
  setViewName: StartRumResult['setViewName']

  setViewContext: StartRumResult['setViewContext']
  setViewContextProperty: StartRumResult['setViewContextProperty']
  getViewContext: StartRumResult['getViewContext']

  globalContext: ContextManager
  userContext: ContextManager
  accountContext: ContextManager

  addAction: StartRumResult['addAction']
  startAction: StartRumResult['startAction']
  stopAction: StartRumResult['stopAction']
  startResource: StartRumResult['startResource']
  stopResource: StartRumResult['stopResource']
  addError: StartRumResult['addError']
  addFeatureFlagEvaluation: StartRumResult['addFeatureFlagEvaluation']
  startDurationVital: StartRumResult['startDurationVital']
  stopDurationVital: StartRumResult['stopDurationVital']
  addDurationVital: StartRumResult['addDurationVital']
  addOperationStepVital: StartRumResult['addOperationStepVital']
}

export function makeRumPublicApi(
  recorderApi: RecorderApi,
  profilerApi: ProfilerApi,
  options: RumPublicApiOptions = {}
): RumPublicApi {
  const trackingConsentState = createTrackingConsentState()
  const bufferedData = startBufferingData()

  let strategy = createPreStartStrategy(
    options,
    trackingConsentState,
    (configuration, sessionManager, deflateWorker, initialViewOptions, telemetry, hooks) => {
      const createEncoder =
        deflateWorker && options.createDeflateEncoder
          ? (streamId: DeflateEncoderStreamId) => options.createDeflateEncoder!(deflateWorker, streamId)
          : createIdentityEncoder

      const startRumResult = mockable(startRum)(
        configuration,
        sessionManager,
        recorderApi,
        profilerApi,
        initialViewOptions,
        createEncoder,
        bufferedData.observable,
        telemetry,
        hooks,
        options.sdkName
      )

      recorderApi.onRumStart(
        startRumResult.lifeCycle,
        configuration,
        sessionManager,
        startRumResult.viewHistory,
        deflateWorker,
        startRumResult.telemetry
      )

      profilerApi.onRumStart(
        startRumResult.lifeCycle,
        startRumResult.hooks,
        configuration,
        sessionManager,
        startRumResult.viewHistory,
        createEncoder
      )

      strategy = createPostStartStrategy(strategy, startRumResult)

      callPluginsOnRumStart(configuration.plugins, {
        addEvent: startRumResult.addEvent,
        addError: startRumResult.addError,
      })

      return startRumResult
    }
  )
  const getStrategy = () => strategy

  const startView: {
    (name?: string): void
    (options: ViewOptions): void
  } = (options?: string | ViewOptions) => {
    const handlingStack = createHandlingStack('view')
    callMonitored(() => {
      const sanitizedOptions = typeof options === 'object' ? options : { name: options }
      strategy.startView({
        ...sanitizedOptions,
        name: sanitizeStringOption(sanitizedOptions.name, 'view name'),
        service: sanitizeStringOption(sanitizedOptions.service, 'view service'),
        version: sanitizeStringOption(sanitizedOptions.version, 'view version'),
        handlingStack,
      })
      addTelemetryUsage({ feature: 'start-view' })
    })
  }

  const startOperation: RumPublicApi['startOperation'] = (name, options) => {
    const handlingStack = createHandlingStack('vital')
    callMonitored(() => {
      addTelemetryUsage({ feature: 'add-operation-step-vital', action_type: 'start' })
      strategy.addOperationStepVital(name, 'start', { ...options, handlingStack })
    })
  }

  const succeedOperation: RumPublicApi['succeedOperation'] = monitor((name, options) => {
    addTelemetryUsage({ feature: 'add-operation-step-vital', action_type: 'succeed' })
    strategy.addOperationStepVital(name, 'end', options)
  })

  const failOperation: RumPublicApi['failOperation'] = monitor((name, failureReason, options) => {
    addTelemetryUsage({ feature: 'add-operation-step-vital', action_type: 'fail' })
    strategy.addOperationStepVital(name, 'end', options, failureReason)
  })

  const rumPublicApi: RumPublicApi = makePublicApi<RumPublicApi>({
    init: (initConfiguration) => {
      const errorStack = new Error().stack
      callMonitored(() => strategy.init(initConfiguration, rumPublicApi, errorStack))
    },

    setTrackingConsent: monitor((trackingConsent) => {
      trackingConsentState.update(trackingConsent)
      addTelemetryUsage({ feature: 'set-tracking-consent', tracking_consent: trackingConsent })
    }),

    setViewName: monitor((name: string) => {
      strategy.setViewName(sanitizeStringOption(name, 'view name')!)
      addTelemetryUsage({ feature: 'set-view-name' })
    }),

    setViewContext: monitor((context: Context) => {
      strategy.setViewContext(context)
      addTelemetryUsage({ feature: 'set-view-context' })
    }),

    setViewContextProperty: monitor((key: string, value: any) => {
      strategy.setViewContextProperty(key, value)
      addTelemetryUsage({ feature: 'set-view-context-property' })
    }),

    getViewContext: monitor(() => {
      addTelemetryUsage({ feature: 'set-view-context-property' })
      return strategy.getViewContext()
    }),

    getInternalContext: monitor((startTime) => strategy.getInternalContext(startTime)),

    getInitConfiguration: monitor(() => deepClone(strategy.initConfiguration)),

    addAction: (name, context) => {
      const handlingStack = createHandlingStack('action')

      callMonitored(() => {
        strategy.addAction({
          name: sanitize(name)!,
          context: sanitize(context) as Context,
          startClocks: clocksNow(),
          type: ActionType.CUSTOM,
          handlingStack,
        })
        addTelemetryUsage({ feature: 'add-action' })
      })
    },

    startAction: monitor((name, options) => {
      addTelemetryUsage({ feature: 'start-action' })
      strategy.startAction(sanitize(name)!, {
        type: sanitize(options?.type) as ActionType | undefined,
        context: sanitize(options?.context) as Context,
        actionKey: options?.actionKey,
      })
    }),

    stopAction: monitor((name, options) => {
      addTelemetryUsage({ feature: 'stop-action' })
      strategy.stopAction(sanitize(name)!, {
        type: sanitize(options?.type) as ActionType | undefined,
        context: sanitize(options?.context) as Context,
        actionKey: options?.actionKey,
      })
    }),

    startResource: monitor((url, options) => {
      addTelemetryUsage({ feature: 'start-resource' })
      strategy.startResource(sanitize(url)!, {
        type: sanitize(options?.type) as ResourceType | undefined,
        method: sanitize(options?.method) as string | undefined,
        context: sanitize(options?.context) as Context,
        resourceKey: options?.resourceKey,
      })
    }),

    stopResource: monitor((url, options) => {
      addTelemetryUsage({ feature: 'stop-resource' })
      strategy.stopResource(sanitize(url)!, {
        type: sanitize(options?.type) as ResourceType | undefined,
        statusCode: options?.statusCode,
        size: options?.size,
        context: sanitize(options?.context) as Context,
        resourceKey: options?.resourceKey,
      })
    }),

    addError: (error, context) => {
      const handlingStack = createHandlingStack('error')
      callMonitored(() => {
        strategy.addError({
          error, // Do not sanitize error here, it is needed unserialized by computeRawError()
          handlingStack,
          context: sanitize(context) as Context,
          startClocks: clocksNow(),
        })
        addTelemetryUsage({ feature: 'add-error' })
      })
    },

    addTiming: monitor((name, time) => {
      // TODO: next major decide to drop relative time support or update its behaviour
      strategy.addTiming(sanitize(name)!, time as RelativeTime | TimeStamp | undefined)
    }),

    setViewLoadingTime: monitor(() => {
      const callTimestamp = timeStampNow()
      strategy.setLoadingTime(callTimestamp)
      addTelemetryUsage({
        feature: 'addViewLoadingTime',
      })
    }),

    setGlobalContext: defineContextMethod(
      getStrategy,
      CustomerContextKey.globalContext,
      ContextManagerMethod.setContext,
      'set-global-context'
    ),
    getGlobalContext: defineContextMethod(
      getStrategy,
      CustomerContextKey.globalContext,
      ContextManagerMethod.getContext,
      'get-global-context'
    ),
    setGlobalContextProperty: defineContextMethod(
      getStrategy,
      CustomerContextKey.globalContext,
      ContextManagerMethod.setContextProperty,
      'set-global-context-property'
    ),
    removeGlobalContextProperty: defineContextMethod(
      getStrategy,
      CustomerContextKey.globalContext,
      ContextManagerMethod.removeContextProperty,
      'remove-global-context-property'
    ),
    clearGlobalContext: defineContextMethod(
      getStrategy,
      CustomerContextKey.globalContext,
      ContextManagerMethod.clearContext,
      'clear-global-context'
    ),

    setUser: defineContextMethod(
      getStrategy,
      CustomerContextKey.userContext,
      ContextManagerMethod.setContext,
      'set-user'
    ),
    getUser: defineContextMethod(
      getStrategy,
      CustomerContextKey.userContext,
      ContextManagerMethod.getContext,
      'get-user'
    ),
    setUserProperty: defineContextMethod(
      getStrategy,
      CustomerContextKey.userContext,
      ContextManagerMethod.setContextProperty,
      'set-user-property'
    ),
    removeUserProperty: defineContextMethod(
      getStrategy,
      CustomerContextKey.userContext,
      ContextManagerMethod.removeContextProperty,
      'remove-user-property'
    ),
    clearUser: defineContextMethod(
      getStrategy,
      CustomerContextKey.userContext,
      ContextManagerMethod.clearContext,
      'clear-user'
    ),

    setAccount: defineContextMethod(
      getStrategy,
      CustomerContextKey.accountContext,
      ContextManagerMethod.setContext,
      'set-account'
    ),
    getAccount: defineContextMethod(
      getStrategy,
      CustomerContextKey.accountContext,
      ContextManagerMethod.getContext,
      'get-account'
    ),
    setAccountProperty: defineContextMethod(
      getStrategy,
      CustomerContextKey.accountContext,
      ContextManagerMethod.setContextProperty,
      'set-account-property'
    ),
    removeAccountProperty: defineContextMethod(
      getStrategy,
      CustomerContextKey.accountContext,
      ContextManagerMethod.removeContextProperty,
      'remove-account-property'
    ),
    clearAccount: defineContextMethod(
      getStrategy,
      CustomerContextKey.accountContext,
      ContextManagerMethod.clearContext,
      'clear-account'
    ),

    startView,

    stopSession: monitor(() => {
      strategy.stopSession()
      addTelemetryUsage({ feature: 'stop-session' })
    }),

    addFeatureFlagEvaluation: monitor((key, value) => {
      strategy.addFeatureFlagEvaluation(sanitize(key)!, sanitize(value))
      addTelemetryUsage({ feature: 'add-feature-flag-evaluation' })
    }),

    getSessionReplayLink: monitor(() => recorderApi.getSessionReplayLink()),

    startSessionReplayRecording: monitor((options?: StartRecordingOptions) => {
      recorderApi.start(options)
      addTelemetryUsage({ feature: 'start-session-replay-recording', force: options?.force })
    }),

    stopSessionReplayRecording: monitor(() => recorderApi.stop()),

    addDurationVital: (name, options) => {
      const handlingStack = createHandlingStack('vital')
      callMonitored(() => {
        addTelemetryUsage({ feature: 'add-duration-vital' })
        strategy.addDurationVital({
          id: generateUUID(),
          name: sanitize(name)!,
          type: VitalType.DURATION,
          startClocks: timeStampToClocks(options.startTime as TimeStamp),
          duration: options.duration as Duration,
          context: sanitize(options?.context) as Context,
          description: sanitize(options?.description) as string | undefined,
          handlingStack,
        })
      })
    },

    startDurationVital: (name, options) => {
      const handlingStack = createHandlingStack('vital')
      callMonitored(() => {
        addTelemetryUsage({ feature: 'start-duration-vital' })
        strategy.startDurationVital(sanitize(name)!, {
          vitalKey: options?.vitalKey,
          context: sanitize(options?.context) as Context,
          description: sanitize(options?.description) as string | undefined,
          handlingStack,
        })
      })
    },

    stopDurationVital: monitor((name, options) => {
      addTelemetryUsage({ feature: 'stop-duration-vital' })
      strategy.stopDurationVital(sanitize(name)!, {
        vitalKey: options?.vitalKey,
        context: sanitize(options?.context) as Context,
        description: sanitize(options?.description) as string | undefined,
      })
    }),

    startOperation,
    succeedOperation,
    failOperation,

    // Deprecated aliases — kept for backwards compatibility, forward to the renamed APIs above.
    // TODO: remove in the next major version (RUM-16921).
    startFeatureOperation: startOperation,
    succeedFeatureOperation: succeedOperation,
    failFeatureOperation: failOperation,

    DEFAULT_TRACKED_RESOURCE_HEADERS,
  })

  return rumPublicApi
}

function sanitizeStringOption(value: unknown, label: string): string | undefined {
  if (value === undefined) {
    return undefined
  }
  if (typeof value !== 'string') {
    display.warn(`Invalid ${label} provided (expected a string, got ${typeof value}). Ignoring.`)
    return undefined
  }
  return sanitize(value)
}

function createPostStartStrategy(preStartStrategy: Strategy, startRumResult: StartRumResult): Strategy {
  return {
    ...preStartStrategy,
    init: (initConfiguration: RumInitConfiguration) => {
      displayAlreadyInitializedError('DD_RUM', initConfiguration)
    },
    getInternalContext: startRumResult.getInternalContext,
    stopSession: startRumResult.stopSession,
    getViewContext: startRumResult.getViewContext,
    globalContext: startRumResult.globalContext,
    userContext: startRumResult.userContext,
    accountContext: startRumResult.accountContext,
  }
}
